/**
 * SetCommunityLiveWatchersUseCase
 *
 * Increment (join) or decrement (leave) the live message's watcher count. A thin
 * single-action passthrough to `LiveStreamingService.setLiveWatchers`, fired by
 * the Task-10 audience hook: `true` on a successful audience join, `false` on the
 * audience's `disposeStreaming` teardown.
 *
 * Web analog of Flutter's `live_streaming_screen.setLiveWatchers`
 * (lib/presentation/screens/community/live_streaming_screen.dart:835-840):
 *   `StreamingRepository().setLiveWatchers(widget.liveStreamingMessageId, joined)`.
 * Called with `true` on audience join success (`:439`) and `false` on the
 * audience-only `disposeStreaming` (`:249-251`,
 * `!_isBroadcaster && !_isOpeningChannel`). The `joined` flag decision + the
 * broadcaster/opening-channel gates live in the hook (Task 10); this use case is
 * the pure single-action bridge.
 *
 * Constructor-injected service (video-call use-case pattern) for testability.
 */

import {
  LiveStreamingService,
  liveStreamingService,
} from '@/domain/services/liveStreaming/liveStreamingService';

export interface SetCommunityLiveWatchersParams {
  /** The live message `id` whose `broadcastingInfo.watchers` is adjusted. */
  liveStreamingMessageId: string;
  /** true increments (+1) on join, false decrements (-1) on leave. */
  joined: boolean;
}

export class SetCommunityLiveWatchersUseCase {
  private service: LiveStreamingService;

  constructor(service: LiveStreamingService = liveStreamingService) {
    this.service = service;
  }

  /**
   * Adjust the watcher count on the live message.
   *
   * @returns Promise<boolean> — the service's write result, propagated verbatim.
   */
  async execute(params: SetCommunityLiveWatchersParams): Promise<boolean> {
    const { liveStreamingMessageId, joined } = params;
    return this.service.setLiveWatchers(liveStreamingMessageId, joined);
  }
}

// Export singleton instance
export const setCommunityLiveWatchersUseCase = new SetCommunityLiveWatchersUseCase();
